import { Link } from "react-router-dom";
import { Phone, Mail, Instagram } from "lucide-react";
import { BRAND, SERVICES } from "@/lib/site";

export default function Footer() {
  return (
    <footer data-testid="site-footer" className="bg-navy text-ivory">
      <div className="mx-auto max-w-[1440px] px-6 py-16 lg:px-10 lg:py-20">
        <p className="font-serif text-3xl sm:text-4xl font-light tracking-tight text-ivory/95">
          Locally Managed.<br />
          <span className="italic text-bermuda">Beautifully Hosted.</span>
        </p>

        <div className="mt-14 grid gap-12 md:grid-cols-2 lg:grid-cols-4">
          <div>
            <img src="/logo-horizontal-white.png" alt={BRAND.name} className="h-12 w-auto" />
            <p className="mt-4 text-sm leading-relaxed text-ivory/60">
              Bermuda vacation rental management, Airbnb co-hosting, cleaning, maintenance and property care for homeowners across the island.
            </p>
            <div className="mt-6 flex flex-col gap-2.5 text-sm">
              <a href={BRAND.phoneHref} data-testid="footer-phone" className="flex items-center gap-2.5 text-ivory/80 hover:text-bermuda transition-colors">
                <Phone size={14} /> {BRAND.phone}
              </a>
              <a href={`mailto:${BRAND.email}`} data-testid="footer-email" className="flex items-center gap-2.5 text-ivory/80 hover:text-bermuda transition-colors">
                <Mail size={14} /> {BRAND.email}
              </a>
              <a href={BRAND.instagram} target="_blank" rel="noopener noreferrer" data-testid="footer-instagram" className="flex items-center gap-2.5 text-ivory/80 hover:text-bermuda transition-colors">
                <Instagram size={14} /> Instagram
              </a>
            </div>
          </div>

          <div>
            <h4 className="text-[11px] font-semibold uppercase tracking-[0.25em] text-bermuda">Services</h4>
            <ul className="mt-5 space-y-2.5 text-sm text-ivory/70">
              {SERVICES.slice(0, 6).map((s) => (
                <li key={s.slug}>
                  <Link to={s.path} data-testid={`footer-service-${s.slug}`} className="hover:text-ivory transition-colors">{s.title}</Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-[11px] font-semibold uppercase tracking-[0.25em] text-bermuda">Company</h4>
            <ul className="mt-5 space-y-2.5 text-sm text-ivory/70">
              <li><Link to="/about" data-testid="footer-about-link" className="hover:text-ivory transition-colors">About</Link></li>
              <li><Link to="/for-homeowners" data-testid="footer-homeowners-link" className="hover:text-ivory transition-colors">For Homeowners</Link></li>
              <li><Link to="/pricing" data-testid="footer-pricing-link" className="hover:text-ivory transition-colors">Pricing</Link></li>
              <li><Link to="/resources" data-testid="footer-resources-link" className="hover:text-ivory transition-colors">Resource Center</Link></li>
              <li><Link to="/faq" data-testid="footer-faq-link" className="hover:text-ivory transition-colors">FAQ</Link></li>
              <li><Link to="/contact" data-testid="footer-contact-link" className="hover:text-ivory transition-colors">Contact</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="text-[11px] font-semibold uppercase tracking-[0.25em] text-bermuda">Owners & Guests</h4>
            <ul className="mt-5 space-y-2.5 text-sm text-ivory/70">
              <li><Link to="/owner/login" data-testid="footer-owner-login-link" className="hover:text-ivory transition-colors">Property Owner Login</Link></li>
              <li><Link to="/guest-support" data-testid="footer-guest-support-link" className="hover:text-ivory transition-colors">Guest Support</Link></li>
              <li><Link to="/guest-tools" data-testid="footer-guest-tools-link" className="hover:text-ivory transition-colors">Guest Tools</Link></li>
              <li><Link to="/bermuda-bucket-list" data-testid="footer-bucket-list-link" className="hover:text-ivory transition-colors">Bucket List Challenge</Link></li>
              <li><Link to="/loyalty" data-testid="footer-loyalty-link" className="hover:text-ivory transition-colors">The Co-Host Circle</Link></li>
              <li><Link to="/guides" data-testid="footer-guides-link" className="hover:text-ivory transition-colors">Digital Guides &amp; Ebooks</Link></li>
              <li><Link to="/pay" data-testid="footer-pay-link" className="hover:text-ivory transition-colors">Make a Payment</Link></li>
              <li><Link to="/reviews" data-testid="footer-reviews-link" className="hover:text-ivory transition-colors">Leave a Review</Link></li>
              <li><Link to="/assessment" data-testid="footer-assessment-link" className="hover:text-ivory transition-colors">Get a Property Assessment</Link></li>
              <li><Link to="/consultation" data-testid="footer-consultation-link" className="hover:text-ivory transition-colors">Book a Consultation</Link></li>
            </ul>
          </div>
        </div>

        <div className="mt-16 flex flex-col gap-4 border-t border-white/10 pt-8 text-xs text-ivory/45 sm:flex-row sm:items-center sm:justify-between">
          <p>© {new Date().getFullYear()} {BRAND.name} · Bermuda Vacation Rental Management</p>
          <div className="flex gap-6">
            <Link to="/privacy" data-testid="footer-privacy-link" className="hover:text-ivory transition-colors">Privacy Policy</Link>
            <Link to="/terms" data-testid="footer-terms-link" className="hover:text-ivory transition-colors">Terms</Link>
            <Link to="/disclaimer" data-testid="footer-disclaimer-link" className="hover:text-ivory transition-colors">Disclaimer</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
